import React from "react";
import { ChangeEvent, FormEvent } from "react";
import { Prompt } from "react-router";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import { V2, apiGet, apiPatch, apiPost } from "../../Api";
import { dateFromLocalISOString, formatName, formatTelephonePartial, TIME_ISO_BARE, validateEmailOrNone, validatePhoneNumber, formatTimeLocal } from "../../Utilities";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import ValidateInput from "../ValidateInput";
import FileUploadDialog from "../FileUploadDialog";

import "../../blocks.css";
import "../../button.css";
import "../../form.css";


interface ICreateAppointmentState {
    birthDate: string;
    nameFirst: string;
    nameLast: string;
    ssn: string;

    apptDate: string;
    apptTime: string;

    phone: string;
    email: string;

    chartNumber: string;
    sex: string;

    apptType: string;
    insuranceProvider: string;
    location: string;
    notes: string;
    nurse: string;
    tech: string;
    surgeryLikely: boolean;
    surgerySuggestion: string;

    existingId?: string,
    fairId?: string;
    isFromBase: boolean;
    isFromConsult: boolean;
    surveyId?: string;
    consultId?: string;
    survey?: any;

    conflictingAppts: number;
    nurses: any[];
    techs: any[];
    locations: any[];
    marketers: any[];
    insuranceProviders: any[];
    appointmentTypes: any[];

    inputs: ValidateInput[];
    submitPending: boolean | string;

    changedFields: string[];
    error?: string;
    hasChanged: boolean;
    isEdit: boolean;
    isLoading: boolean;
    isUploadDialogOpen: boolean;

    scheduleWarning?: string;
    submitBlocked: boolean;

    displayDenied: boolean;

    assignTo: string;

    pelletFirstVisit: boolean;

    proofFileId?: string;
    healthFairInfo: any;

    allowNoProof: boolean;

    allowEditBirthdate: boolean;
    allowEditName: boolean;
    allowEditSex: boolean;

    [name: string]: any;
}

interface ICreationAppointmentViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
}

class CreateAppointmentView extends React.Component<ICreationAppointmentViewProps, ICreateAppointmentState> {
    private apptLookupTimer?: number;


    constructor(props: Readonly<ICreationAppointmentViewProps>) {
        super(props);

        this.state = {
            birthDate: "",

            nameFirst: "",
            nameLast: "",
            ssn: "",

            apptDate: "",
            apptTime: "",

            email: "",
            phone: "",

            chartNumber: "",
            sex: "",

            apptType: "new_annual",
            insuranceProvider: "",
            location: "",
            notes: "",
            nurse: "",
            tech: "",
            surgeryLikely: false,
            surgerySuggestion: "",

            existingId: undefined,
            fairId: undefined,
            isFromBase: false,
            isFromConsult: false,
            surveyId: undefined,
            consultId: undefined,

            conflictingAppts: 0,
            locations: [],
            marketers: [],
            nurses: [],
            techs: [],
            insuranceProviders: [],
            appointmentTypes: [],

            inputs: [],
            submitPending: false,

            changedFields: [],
            hasChanged: false,
            isEdit: false,
            isLoading: true,
            isUploadDialogOpen: false,
            submitBlocked: false,

            assignTo: "",
            healthFairInfo: {},

            pelletFirstVisit: false,

            allowNoProof: (this.props.location.state && this.props.location.state.allowNoProof) ||
                props.authContext.hasRight("appt:create:noproof")
                || false,

            displayDenied:
                (!this.props.location.state && !props.authContext.hasRight("appt:create:bare")) || // Disallow if no state and no allowed to create bare appointment
                (this.props.location.state && this.props.location.state.appointment && !props.authContext.hasRight("appt:write")), // Also disallow if there is state with appointment (edit), but you don't have appointment edit permissions

            allowEditBirthdate: false,
            allowEditName: false,
            allowEditSex: false,
        };

        this.formInputChanged = this.formInputChanged.bind(this);
        this.formInputChangedScheduleTime = this.formInputChangedScheduleTime.bind(this);
        this.formAppointmentTypeChanged = this.formAppointmentTypeChanged.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.lookupAppointments = this.lookupAppointments.bind(this);
        this.toggleUploadDialog = this.toggleUploadDialog.bind(this);
        this.handleUploadSuccess = this.handleUploadSuccess.bind(this);
    }

    public async componentDidMount() {
        const newState: Partial<ICreateAppointmentState> = {
            isLoading: false,
        };

        if (this.props.location.state) {
            if (this.props.location.state.survey || this.props.location.state.surveyId) {
                // Creating an appointment from a survey
                let survey = this.props.location.state.survey;
                let healthFairInfo = {};

                if (this.props.location.state.surveyId) {
                    // Creating an appointment from a survey, but we must load it ourselves
                    try {
                        survey = await apiGet<any>(`survey/${this.props.location.state.surveyId}?questions=full`);
                    }
                    catch (e) {
                        this.setState({
                            error: e.message,
                            isLoading: false,
                        });
                        return;
                    }
                }

                if (survey.healthfairId) {
                    // Load health fair info
                    try {
                        healthFairInfo = await apiGet<any>(`fairs/${survey.healthfairId}`);
                    }
                    catch (e) {
                        this.setState({
                            error: e.message,
                            isLoading: false,
                        });
                        return;
                    }
                }

                if (survey.birthdate) {
                    // Trim and handle year 0001 birthdates
                    survey.birthdate = survey.birthdate.substr(0, 10);
                    if (new Date(survey.birthdate)?.getFullYear() < 1800) {
                        survey.birthdate = "";
                    }
                }

                Object.assign(newState, {
                    birthDate: survey.birthdate,
                    email: survey.email,
                    insuranceProvider: survey.insurance.provider,
                    nameFirst: survey.name.first,
                    nameLast: survey.name.last,
                    phone: formatTelephonePartial(survey.phone),
                    sex: survey.sex,
                    chartNumber: survey.outreachLeadInfo?.chartNumber || "",

                    allowEditName: !survey.name.first || !survey.name.last,
                    allowEditBirthdate: !survey.birthdate,
                    allowEditSex: !survey.sex,

                    fairId: survey.healthfairId,
                    isFromBase: true,
                    surveyId: survey.id,

                    location: "",
                    nurse: "",

                    survey,
                    healthFairInfo,
                    allowNoProof:
                        !this.state.healthFairInfo.scheduledDate ||
                        DateTime.fromISO(this.state.healthFairInfo.scheduledDate).hasSame(DateTime.local(), "day") ||
                        !!this.state.healthFairInfo.isActive ||
                        !!this.state.healthFairInfo.isDigitalActive ||
                        this.props.authContext.hasRight("appt:create:noproof")
                });
            }
            else if (this.props.location.state.appointment) {
                // Edit an appointment
                const appt = this.props.location.state.appointment;
                const dateTime = DateTime.fromISO(appt.appointmentTimestamp).toLocal();

                Object.assign(newState, {
                    apptDate: dateTime.toISODate(),
                    apptTime: dateTime.toISOTime(TIME_ISO_BARE),
                    apptType: appt.appointmentType.type,
                    assignTo: appt.employeeId,
                    birthDate: appt.patient.birthDate.substr(0, 10),
                    email: appt.patient.email,
                    chartNumber: appt.patient.chartNumber,
                    sex: appt.patient.sex,
                    existingId: this.props.location.state.appointment.id,
                    fairId: appt.fairId,
                    insuranceProvider: appt.patient.insuranceProvider,
                    isEdit: true,
                    isFromBase: !!appt.surveyId,
                    location: appt.locationId,
                    nameFirst: appt.patient.name.first,
                    nameLast: appt.patient.name.last,
                    nurse: appt.nurseId,
                    tech: appt.techId,
                    phone: appt.patient.phone,
                    surgeryLikely: appt.isSurgeryCandidate || "",
                    surgerySuggestion: appt.surgeryEstimate || "",
                    surveyId: appt.questionnaireId,
                }, () => this.lookupAppointments());
            }
            else if (this.props.location.state.consult) {
                // Pellet appointment from consult
                const consult = this.props.location.state.consult;

                Object.assign(newState, {
                    birthDate: consult.patient.birthDate.substr(0, 10),
                    email: consult.patient.email,
                    insuranceProvider: consult.patient.insuranceProvider,
                    nameFirst: consult.patient.name.first,
                    nameLast: consult.patient.name.last,
                    phone: formatTelephonePartial(consult.patient.phone),
                    sex: consult.patient.sex,
                    assignTo: consult.referrerId,

                    isFromBase: true,
                    isFromConsult: true,
                    surveyId: consult.surveyId,
                    consultId: consult.id,
                    apptType: "pellet",

                    location: "",
                    nurse: "",
                    tech: "",
                    allowNoProof: true,
                });
            }
        }

        try {
            const [locations, nurses, techs, insuranceProviders, appointmentTypes] = await Promise.all([
                apiGet<any[]>("locations"),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.lookupProvider(newState.apptType || "")}`),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.lookupTechnician(newState.apptType || "") || "0"}`),
                V2.get<any[]>("accounting/insurance-companies/summary"),
                V2.get<any[]>("appointments/types")
            ]);
            locations.unshift({ id: "", name: "" });
            nurses.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
                .unshift({ id: "", name: { first: "", last: "" } });
            techs.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
                .unshift({ id: "", name: { first: "", last: "" } });

            let marketers = [];
            if (this.props.authContext.hasRight("appt:create:delegated")) {
                marketers = await apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`);
                marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)));
            }

            newState.locations = locations;
            newState.marketers = marketers;
            newState.nurses = nurses;
            newState.techs = techs;
            newState.insuranceProviders = insuranceProviders;
            newState.appointmentTypes = appointmentTypes;

            this.setState(newState);
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false,
            });
        }

    }

    public componentWillUnmount() {
        window.clearTimeout(this.apptLookupTimer);
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.error}</span>
                </div>
            );
        }

        if (this.state.displayDenied) {
            return (
                <div className="viewer-root">
                    <div className="warning-box">You are not allowed to create an office appointment without doing so through a <b>health fair questionnaire</b>.</div>
                </div>
            );
        }

        const isStandalone = !this.state.surveyId;

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <FileUploadDialog isOpen={this.state.isUploadDialogOpen} onRequestClose={this.toggleUploadDialog} onUploadSuccess={this.handleUploadSuccess} closeOnSuccess={true} descriptionOverride="Appointment Proof" accept="image/jpeg,image/png" allowCompression={true} overrideCompression="image" title="Image Upload" allowMultiple={false} />
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <div className="row-root">
                            <section className="info-block">
                                <header>
                                    <h3>Person Details</h3>
                                </header>

                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-name-first" className="required">First Name</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name-first" name="nameFirst" value={this.state.nameFirst} onChange={this.formInputChanged} placeholder="First Name" autoComplete="off_please_chrome" disabled={(this.state.isFromBase && !this.state.allowEditName) || (this.state.isEdit && !isStandalone)} isRequired={true} />
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-name-last" className="required">Last Name</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name-last" name="nameLast" value={this.state.nameLast} onChange={this.formInputChanged} placeholder="Last Name" autoComplete="off_please_chrome" disabled={(this.state.isFromBase && !this.state.allowEditName) || (this.state.isEdit && !isStandalone)} isRequired={true} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group col-50">
                                        <label htmlFor="frm-birthdate" className="required">Date of Birth</label>
                                        <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-birthdate" name="birthDate" value={this.state.birthDate} onChange={this.formInputChanged} placeholder="mm/dd/yyyy" autoComplete="off_please_chrome" disabled={(this.state.isFromBase && !this.state.allowEditBirthdate) || (this.state.isEdit && !isStandalone)} isRequired={true} validator={this.validateBirthdate} invalidMessage="Please double-check date" />
                                    </div>
                                    <div className={`input-form-group col-${!this.props.authContext.serverType ? "25" : "50"}`}>
                                        <label htmlFor="frm-sex">Sex</label>
                                        <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-sex" name="sex" value={this.state.sex} onChange={this.formInputChanged} autoComplete="off_please_chrome" disabled={(this.state.isFromBase && !this.state.allowEditSex) || (this.state.isEdit && !isStandalone)} isRequired={true}>
                                            <option value="" />
                                            <option value="F">Female</option>
                                            <option value="M">Male</option>
                                        </ValidateInput>
                                    </div>
                                    {
                                        !this.props.authContext.serverType
                                            ? <div className="input-form-group col-25">
                                                <label htmlFor="frm-chart" className="required">Chart #</label>
                                                <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-chart" name="chartNumber" value={this.state.chartNumber} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                            </div>
                                            : null
                                    }
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-phone" className="required">Phone Number</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-phone" name="phone" value={this.state.phone} onChange={this.formInputChanged} placeholder="(xxx) xxx-xxxx" autoComplete="off_please_chrome" filter={formatTelephonePartial} isRequired={true} requiredMessage="Phone number is required" validator={validatePhoneNumber} disabled={(this.state.isEdit && !isStandalone)} />
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-email" className="required">Email Address</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-email" name="email" value={this.state.email} onChange={this.formInputChanged} placeholder="Email Address" autoComplete="off_please_chrome" validator={validateEmailOrNone} invalidMessage="Invalid Email address" isRequired={true} disabled={(this.state.isEdit && !isStandalone)} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-insurance" className="required">Insurance Provider</label>
                                        <ValidateInput type="select-other" ref={this.receiveValidationRef} id="frm-insurance" name="insuranceProvider" value={this.state.insuranceProvider} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} disabled={(this.state.isEdit && !isStandalone)}>
                                            <option value="" />
                                            {this.state.insuranceProviders.map((a: any) => <option key={a.id} value={a.shortName || a.name}>{a.name}</option>)}
                                        </ValidateInput>
                                    </div>
                                </section>
                                {
                                    (this.state.isEdit && !isStandalone)
                                        ? <small><br /><em>To change some of the above values, change them in the source survey.</em></small>
                                        : null
                                }
                            </section>
                            {
                                this.state.survey ? (
                                    <table className="nohover">
                                        <thead>
                                            <tr>
                                                <th>Question</th>
                                                <th>Answer</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                this.state.survey.answers.map((a: any) => (
                                                    <tr key={a.id}>
                                                        <td className="wrap">{a.displayText}</td>
                                                        <td>{a.answerText}</td>
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                    </table>
                                ) : ""
                            }
                        </div>
                        <section className="info-block">
                            <header>
                                <h3>Appointment Details</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-location" className="required">Office Location</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-location" name="location" value={this.state.location} onChange={this.formInputChangedScheduleTime} autoComplete="off_please_chrome" isRequired={true}>
                                        {this.state.locations.map(i => <option key={i.id} value={i.id}>{i.shortName ? `${i.shortName} - ` : ""}{i.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-date" className="required">Date</label>
                                    <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-appt-date" name="apptDate" value={this.state.apptDate} onChange={this.formInputChangedScheduleTime} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-time" className="required">Time</label>
                                    <ValidateInput type="time" ref={this.receiveValidationRef} id="frm-appt-time" name="apptTime" value={this.state.apptTime} onChange={this.formInputChangedScheduleTime} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            {
                                this.state.scheduleWarning
                                    ? <section>
                                        <div className={`input-form-group ${this.state.submitBlocked ? "error-box" : "warning-box"}`}>{this.state.scheduleWarning}</div>
                                    </section>
                                    : ""
                            }
                            <section>
                                <span>There are {this.state.conflictingAppts < 0 ? "??" : this.state.conflictingAppts} appointment(s) at the selected time</span>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-type" className="required">Appointment Type</label>
                                    <select id="frm-appt-type" name="apptType" value={this.state.apptType} onChange={this.formInputChangedScheduleTime} autoComplete="off_please_chrome" disabled={this.state.isFromConsult}>
                                        {this.state.appointmentTypes.map(a => <option key={a.id} value={a.type}>{a.name}</option>)}
                                    </select>
                                </div>
                                <div className="input-form-group" />
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-appt-provider">Provider</label>
                                    <select id="frm-appt-provider" name="nurse" value={this.state.nurse} onChange={this.formInputChangedScheduleTime} autoComplete="off_please_chrome">
                                        {this.state.nurses.map(i => <option key={i.id} value={i.id}>{formatName(i.name)}</option>)}
                                    </select>
                                </div>
                                {
                                    this.state.techs.length > 1
                                        ? <div className="input-form-group">
                                            <label htmlFor="frm-appt-tech">Technician</label>
                                            <select id="frm-appt-tech" name="tech" value={this.state.tech} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                                {this.state.techs.map(i => <option key={i.id} value={i.id}>{formatName(i.name)}</option>)}
                                            </select>
                                        </div>
                                        : null
                                }
                            </section>
                            {
                                this.state.apptType !== "pellet"
                                    ? <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-surgery-likely">Likely Needs Surgery</label>
                                            <div className="input-form-group-check-inline">
                                                <label>
                                                    <input type="checkbox" id="frm-surgery-likely" name="surgeryLikely" checked={this.state.surgeryLikely} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                                    Likely candidate for surgery
                                                </label>
                                            </div>
                                        </div>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-surgery-suggestion">Suggested/Requested Surgery</label>
                                            <input type="text" id="frm-surgery-suggestion" name="surgerySuggestion" value={this.state.surgerySuggestion} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                        </div>
                                    </section>
                                    : <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-pellet-first-visit">First visit</label>
                                            <div className="input-form-group-check-inline">
                                                <label>
                                                    <input type="checkbox" id="frm-pellet-first-visit" name="pelletFirstVisit" checked={this.state.pelletFirstVisit} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                                    Is first visit
                                                </label>
                                            </div>
                                        </div>
                                    </section>
                            }
                            {
                                this.props.authContext.hasRight("appt:create:delegated")
                                    ? <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-assign-to">Assign to</label>
                                            <select id="frm-assign-to" name="assignTo" value={this.state.assignTo} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                                <option value="" />
                                                <option value={this.props.authContext.user.employee.id}>{this.props.authContext.user.displayName} (you)</option>
                                                {this.state.marketers.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                            </select>
                                        </div>
                                    </section>
                                    : ""
                            }
                            {
                                !this.state.isEdit && !this.props.authContext.serverType
                                    ? <>
                                        <section>
                                            <div className="input-form-group">
                                                <label htmlFor="frm-proof">Proof {!this.state.allowNoProof ? <small>(required)</small> : <small>(optional)</small>}</label>
                                                <button type="button" className="button text-with-icon" onClick={this.toggleUploadDialog}><i className="icon-upload" /><span>Upload</span></button>
                                            </div>
                                        </section>
                                        {this.state.proofFileId
                                            ? <section>File uploaded!</section>
                                            : null
                                        }
                                    </>
                                    : null
                            }
                            <section className="section-last">
                                <div className="input-form-group">
                                    <label htmlFor="frm-notes">{this.state.isEdit ? "Additional Notes" : "Notes"}</label>
                                    <textarea className="input-textarea-medium" id="frm-notes" name="notes" value={this.state.notes} onChange={this.formInputChanged} placeholder="Other notes about anything not covered above" autoComplete="off_please_chrome" />
                                </div>
                            </section>
                        </section>
                    </div>
                    &nbsp;
                    {
                        this.state.submitPending && (typeof this.state.submitPending === "string") ? (
                            <span className="error-text">{this.state.submitPending}</span>
                        ) : (
                                this.state.submitPending === true ? (<div className="spinner lds-dual-ring" />) : ""
                            )
                    }
                    <div className="action-panel">
                        <button type="submit" className="button btn-confirm push-right" disabled={this.state.submitPending === true || this.state.submitBlocked}>Submit</button>
                        <button type="button" className="button btn-cancel" onClick={this.cancelForm} disabled={this.state.submitPending === true}>Cancel</button>
                    </div>
                </form>
            </div>
        );
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            s.changedFields.push(name);
            return {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            }
        });
    }

    private formAppointmentTypeChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            s.changedFields.push(name);
            return {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            }
        }, async () => {
            // Look up providers that can perform the selected appointment type
            const [nurses, techs] = await Promise.all([
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.Provider}`),
                apiGet<any[]>(`employees/summary?group=${WellKnownGroups.lookupTechnician(this.state.apptType || "") || "0"}`),
            ]);
            nurses.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
                .unshift({ id: "", name: { first: "", last: "" } });
            techs.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name)))
                .unshift({ id: "", name: { first: "", last: "" } });

            const newState: Partial<ICreateAppointmentState> = {
                nurses,
                nurse: this.state.nurse,
                techs,
                tech: this.state.tech,
            };

            if (!nurses.some(v => v.id === this.state.nurse)) {
                newState.nurse = "";
            }

            if (!techs.some(v => v.id === this.state.tech)) {
                newState.tech = "";
            }

            switch (this.state.apptType) {
                case "uro":
                    newState.surgerySuggestion = "TVT";
                    newState.surgeryLikely = true;
                    break;
                default:
                    break;
            }

            this.setState(newState);
        });
    }

    private formInputChangedScheduleTime(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState(s => {
            s.changedFields.push(name);
            return {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value,
            }
        }, async () => {
            window.clearTimeout(this.apptLookupTimer);

            this.apptLookupTimer = window.setTimeout(this.lookupAppointments, 500);

            if (this.state.apptDate && this.state.apptTime && this.state.location) {
                const targetDate = dateFromLocalISOString(`${this.state.apptDate}T${this.state.apptTime}`)!;

                const parameters = [
                    `after=${targetDate.toISODate()}`,
                    `before=${targetDate.plus({ days: 1 }).toISODate()}`,
                    `location=${this.state.location}`
                ];

                let weekSchedule;
                try {
                    const hours = await V2.get<any[]>(`schedule/office?${parameters.join("&")}`);
                    weekSchedule = hours[0];
                }
                catch (e) {
                    this.setState({
                        scheduleWarning: e.message || "Failed to check schedule",
                        submitBlocked: false
                    });
                    return;
                }

                const location = this.state.locations.find(a => a.id === this.state.location);
                const appointmentType = this.state.appointmentTypes.find(a => a.type === this.state.apptType);
                const isVirtual = !appointmentType.isPhysical;

                var dayPrefix = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"][targetDate.weekday % 7];
                const locationOpen = weekSchedule[`${dayPrefix}OpenTime`];
                const locationClose = weekSchedule[`${dayPrefix}CloseTime`];

                const officeHoursStart = !locationOpen ? undefined : DateTime.fromISO(`${this.state.apptDate}T${locationOpen}`, { zone: "America/New_York" });
                const officeHoursEnd = !locationClose ? undefined : DateTime.fromISO(`${this.state.apptDate}T${locationClose}`, { zone: "America/New_York" });

                let isProviderAvailable;
                if (this.state.nurse) {
                    const parameters = [
                        `after=${targetDate.toISODate()}`,
                        `before=${targetDate.plus({ days: 1 }).toISODate()}`,
                        `employee=${this.state.nurse}`,
                        `location=${this.state.location}`
                    ];

                    try {
                        const entries = await V2.get<any[]>(`schedule?${parameters.join("&")}`);
                        isProviderAvailable = !!entries[0];
                    }
                    catch (e) {
                        this.setState({
                            scheduleWarning: e.message || "Failed to check provider schedule",
                            submitBlocked: false
                        });
                        return;
                    }
                }

                if (targetDate) {
                    if (!officeHoursStart || !officeHoursEnd) {
                        // Office is closed that day
                        this.setState({
                            scheduleWarning: `${location.shortName || location.name} is closed that day`,
                            submitBlocked: true
                        });
                    }
                    else if (targetDate.year < 2000) {
                        // Likely mis-typed 2 digit year
                        this.setState({
                            scheduleWarning: "Selected date is too far in the past!",
                            submitBlocked: true
                        });
                    }
                    else if (!isVirtual && (targetDate < officeHoursStart || targetDate >= officeHoursEnd)) {
                        // Outside of office hours
                        this.setState({
                            scheduleWarning: `${location.shortName || location.name}'s hours are ${formatTimeLocal(officeHoursStart)} - ${formatTimeLocal(officeHoursEnd)}`,
                            submitBlocked: true
                        });
                    }
                    else if (targetDate.diffNow("hours").hours <= -24) {
                        // More than 24 hours in the past, show warning
                        this.setState({
                            scheduleWarning: "Selected date and time is in the past!",
                            submitBlocked: false
                        });
                    }
                    else if (isProviderAvailable === false) {
                        this.setState({
                            scheduleWarning: `Selected provider might not be available in ${location.shortName || location.name} on that day`,
                            submitBlocked: false
                        });
                    }
                    else {
                        this.setState({
                            scheduleWarning: undefined,
                            submitBlocked: false
                        });
                    }
                }
            }
        });
    }

    private lookupAppointments() {
        // Look up schedule for selected time
        if (!this.state.apptDate || !this.state.apptTime) {
            return;
        }
        const date = new Date(`${this.state.apptDate}T${this.state.apptTime}`);

        apiGet<{ count: number }>(`appointments/count?datetime=${date.toISOString().replace("+", "%2B")}&location=${this.state.location}&status=active_or_pending`)
            .then(r => this.setState({ conflictingAppts: r.count }))
            .catch(ex => this.setState({ conflictingAppts: -1 }));
    }

    private submitForm(e: FormEvent<HTMLFormElement>) {
        // Validate inputs
        let allValid = true;
        for (const input of this.state.inputs) {
            allValid = input.validate() && allValid;
        }

        e.preventDefault();

        if (!allValid) {
            return;
        }

        let apptCreationInfo: any = {};
        if (this.state.isEdit) {
            if (this.state.changedFields.some(v => v === "apptDate" || v === "apptTime")) {
                apptCreationInfo.appointmentTimestamp = dateFromLocalISOString(`${this.state.apptDate}T${this.state.apptTime}`);
            }

            if (this.state.changedFields.some(v => v === "nurse")) {
                apptCreationInfo.nurseId = this.state.nurse || undefined;
            }

            if (this.state.changedFields.some(v => v === "tech")) {
                apptCreationInfo.techId = this.state.tech || undefined;
            }

            if (this.state.changedFields.some(v => v === "location")) {
                apptCreationInfo.locationId = this.state.location || undefined;
            }

            if (this.state.changedFields.some(v => v === "notes")) {
                apptCreationInfo.notes = this.state.notes || undefined;
            }

            if (this.state.changedFields.some(v => v === "apptType")) {
                apptCreationInfo.appointmentType = this.state.apptType.toLowerCase() || undefined;
            }

            if (this.state.changedFields.some(v => v === "assignTo")) {
                apptCreationInfo.assignTo = this.state.assignTo || undefined;
            }

            if (this.state.changedFields.some(v => v === "pelletFirstVisit")) {
                apptCreationInfo.pelletFirstVisit = this.state.pelletFirstVisit || undefined;
            }

            if (this.state.changedFields.some(v => v === "surgeryLikely")) {
                apptCreationInfo.surgeryLikely = this.state.surgeryLikely || undefined;
            }

            if (this.state.changedFields.some(v => v === "surgerySuggestion")) {
                apptCreationInfo.surgerySuggestion = this.state.surgerySuggestion || undefined;
            }

            if (this.state.changedFields.some(v => v === "chartNumber")) {
                apptCreationInfo.chartNumber = this.state.chartNumber || undefined;
            }

            if (this.state.changedFields.some(v => v === "nameFirst")) {
                if (!apptCreationInfo.name)
                    apptCreationInfo.name = {};

                apptCreationInfo.name.first = this.state.nameFirst || undefined;
            }

            if (this.state.changedFields.some(v => v === "nameLast")) {
                if (!apptCreationInfo.name)
                    apptCreationInfo.name = {};

                apptCreationInfo.name.last = this.state.nameLast || undefined;
            }

            if (this.state.changedFields.some(v => v === "birthDate")) {
                apptCreationInfo.birthDate = this.state.birthDate || undefined;
            }

            if (this.state.changedFields.some(v => v === "sex")) {
                apptCreationInfo.sex = this.state.sex || undefined;
            }

            if (this.state.changedFields.some(v => v === "chartNumber")) {
                apptCreationInfo.chartNumber = this.state.chartNumber || undefined;
            }

            if (this.state.changedFields.some(v => v === "phone")) {
                apptCreationInfo.phone = this.state.phone || undefined;
            }

            if (this.state.changedFields.some(v => v === "email")) {
                apptCreationInfo.email = this.state.email || undefined;
            }

            if (this.state.changedFields.some(v => v === "insuranceProvider")) {
                apptCreationInfo.insuranceProvider = this.state.insuranceProvider || undefined;
            }
        }
        else {
            if (!this.state.allowNoProof && this.state.surveyId && !this.state.proofFileId) {
                this.setState({
                    submitPending: "Proof must be uploaded"
                });
                return;
            }

            apptCreationInfo = {
                birthDate: new Date(this.state.birthDate),
                email: this.state.email.toLowerCase(),
                name: {
                    first: this.state.nameFirst,
                    last: this.state.nameLast,
                },
                phone: this.state.phone.replace(/[^\d]/g, ""),
                chartNumber: this.state.chartNumber,
                sex: this.state.sex,
                // ssn: this.state.ssn.replace(/[^\d]/g, ""),

                appointmentTimestamp: dateFromLocalISOString(`${this.state.apptDate}T${this.state.apptTime}`),
                appointmentType: this.state.apptType.toLowerCase(),
                fairId: this.state.fairId,
                insuranceProvider: this.state.insuranceProvider,
                locationId: this.state.location,
                notes: this.state.notes,
                nurseId: this.state.nurse || undefined,
                techId: this.state.tech || undefined,
                questionnaireId: this.state.surveyId,
                consultId: this.state.consultId,

                isSurgeryCandidate: this.state.surgeryLikely,
                surgeryEstimate: this.state.surgerySuggestion,

                assignTo: this.state.assignTo,

                pelletFirstVisit: this.state.pelletFirstVisit,

                proofFileId: this.state.proofFileId
            };
        }

        this.setState({ submitPending: true });
        (this.state.isEdit
            ? apiPatch<any>(`appointments/${this.state.existingId}`, apptCreationInfo)
            : apiPost<any>("appointments", apptCreationInfo))
            .then(appt => {
                this.setState({
                    hasChanged: false,
                    submitPending: false,
                });

                if (!this.state.isEdit && appt) {
                    // New appointments from surveys show the summary
                    this.props.history.push(`/appointments/${appt.id}`, { isFromSurvey: true });
                }
                else {
                    this.props.history.go(this.state.isFromBase ? -2 : -1);
                }
            })
            .catch(ex => {
                this.setState({
                    submitPending: ex.message
                });
            });
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.setState(s => {
            s.inputs.push(ref);
            return { inputs: s.inputs };
        });
    }

    private toggleUploadDialog() {
        const isOpen = this.state.isUploadDialogOpen;

        this.setState({
            isUploadDialogOpen: !isOpen
        });
    }

    private handleUploadSuccess(ids: string[]) {
        this.setState({
            proofFileId: ids[0]
        });
    }

    private validateBirthdate(value: string) {
        try {
            const local = DateTime.local();
            const date = DateTime.fromISO(value);

            return date.year > local.year - 100 && date.year < local.year;
        }
        catch (e) {
            return false;
        }
    }
}

export default withAuth(withTitle("Schedule Appointment", CreateAppointmentView));
